/**
 * CrmDataContext — API-backed version.
 *
 * Strategy: React Query fetches real data from the backend. The context
 * exposes the same interface as the mock version so all existing pages
 * work without changes. Mutations call the API then invalidate queries.
 *
 * Pages that reference useCrmData() get real data automatically.
 */
import { createContext, useContext, useCallback, type Context, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, ApiError, emailAccountsApi, fetchAllPages, type ApiEmailAccount } from "@/lib/api";

export const mutationErrorHandler = (action: string) => (err: unknown) => {
  const msg = err instanceof ApiError
    ? `${err.message}${err.body ? ` — ${JSON.stringify(err.body)}` : ""}`
    : err instanceof Error ? err.message : String(err);
  // eslint-disable-next-line no-console
  console.error(`[${action}] failed:`, err);
  toast.error(`${action} failed: ${msg}`);
};

/** A possible duplicate (lead or customer) returned by the create-lead 409 response. */
export interface DuplicateCandidate {
  id: string;
  type: "lead" | "customer";
  name: string;
  email: string | null;
  phone: string | null;
  stage: string | null;
}
import { getStageProbability, resolveProbability } from "@/lib/stageUtils";
import {
  type Deal, type Customer, type Contact, type Lead, type Activity,
  type TeamMember, type RoleDefinition, type Permission, type UserStatus,
  type Team, type AssignmentEvent, type EmailThread, type EmailMessage,
  type EmailAccount, type EmailProvider, type EmailAccountStatus,
  type MasterItem, type EmailTemplate, type DealStakeholder,
  type StageChecklistItem, type BANTScore, type DealStage, type AutomationRule,
  defaultRoles, defaultTeams,
  masterLeadSources, masterLeadStages, masterActivityTypes,
  masterIndustries, masterTags, masterEmailTemplates,
  masterLeadPriorities, masterLeadStatuses, masterProductServices, masterCompanySizes,
  DEAL_STAGE_CHECKLISTS,
} from "@/data/mockData";

// ── API → local shape converters ──────────────────────────────────
function apiLeadToLocal(l: any): Lead {
  const stage = l.stage ?? "New";
  return {
    id: l.id,
    companyName: l.companyName ?? l.company_name ?? "",
    phone: l.phone ?? "",
    email: l.email ?? "",
    source: l.source ?? "",
    owner: l.ownerUserId ?? l.owner_user_id ?? "",
    assignedTo: l.assignedToUserId ?? l.assigned_to_user_id ?? "",
    assignedToAuth0UserId: l.assignedToAuth0UserId ?? l.assigned_to_auth0_user_id ?? null,
    expectedValue: Number(l.expectedValue ?? l.expected_value ?? 0),
    stage,
    stageEnteredAt: l.stageEnteredAt ?? l.stage_entered_at ?? "",
    nextFollowUp: l.nextFollowUp ?? l.next_follow_up ?? "",
    lostReason: l.lostReason ?? l.lost_reason ?? "",
    createdAt: l.createdAt ?? l.created_at ?? "",
    notes: (l.notes ?? []).map((n: any) => ({
      id: n.id,
      content: n.body ?? n.content ?? "",
      author: n.authorId ?? n.author ?? "",
      createdAt: n.createdAt ?? n.created_at ?? "",
    })),
    assignmentHistory: (l.assignments ?? []).map((a: any) => ({
      id: a.id,
      fromUserId: a.fromUserId ?? null,
      fromUserName: a.fromUserId ?? "Previous",
      toUserId: a.toUserId,
      toUserName: a.toUserId,
      byUserId: a.byUserId,
      byUserName: a.byUserId,
      at: a.occurredAt ?? a.occurred_at ?? "",
      reason: a.reason,
      transferEmailAccess: a.transferEmail ?? false,
    })),
    bantScore: l.bant ?? undefined,
    tags: l.tags ?? [],
    nurtureSchedule: undefined,
    contactPersons: (l.contactPersons ?? []).map((c: any) => ({
      id: c.id, name: c.name, phone: c.phone ?? "", email: c.email ?? "", position: c.position ?? "",
    })),
    priority: l.priority ?? "Medium",
    leadStatus: l.leadStatus ?? l.lead_status ?? "Warm",
    requirementDescription: l.requirementDescription ?? l.requirement_description ?? "",
    followUpNotes: l.followUpNotes ?? l.follow_up_notes ?? "",
    reminderAt: l.reminderAt ?? l.reminder_at ?? "",
    productService: l.productService ?? l.product_service ?? "",
    companySize: l.companySize ?? l.company_size ?? "",
    attachments: l.attachments ?? [],
    conversionProbability: resolveProbability(
      l.conversionProbability ?? l.conversion_probability,
      stage,
      masterLeadStages,
    ),
    estimatedClosureDate: l.estimatedClosureDate ?? l.estimated_closure_date ?? "",
    displayId: l.displayId ?? l.display_id ?? undefined,
  } as any;
}

function apiCustomerToLocal(c: any): Customer {
  return {
    id: c.id,
    name: c.name,
    industry: c.industry ?? "",
    gstNumber: c.gstNumber ?? c.gst_number ?? "",
    address: c.address ?? "",
    accountOwner: c.accountOwnerId ?? c.account_owner_id ?? "",
    tags: [],
    contactCount: Number(c.contactCount ?? c.contact_count ?? 0),
    dealCount: Number(c.dealCount ?? c.deal_count ?? 0),
    totalRevenue: Number(c.totalRevenue ?? c.total_revenue ?? 0),
    createdAt: c.createdAt ?? c.created_at ?? "",
    erpSummary: {
      totalRevenue: Number(c.totalRevenue ?? 0),
      outstandingBalance: Number(c.erpOutstanding ?? c.erp_outstanding ?? 0),
      creditLimit: Number(c.erpCreditLimit ?? c.erp_credit_limit ?? 0),
      lastOrderDate: c.erpLastOrderDate ?? c.erp_last_order_date ?? "",
    },
  } as any;
}

function apiContactToLocal(c: any): Contact {
  return {
    id: c.id,
    firstName: c.firstName ?? c.first_name ?? "",
    lastName: c.lastName ?? c.last_name ?? "",
    name: `${c.firstName ?? c.first_name ?? ""} ${c.lastName ?? c.last_name ?? ""}`.trim(),
    email: c.email ?? "",
    phone: c.phone ?? "",
    position: c.position ?? "",
    customerId: c.customerId ?? c.customer_id ?? "",
    customerName: c.customerId ?? "",
    createdAt: c.createdAt ?? c.created_at ?? "",
    owner: c.ownerId ?? c.owner_id ?? "",
    tags: [],
    description: c.description ?? "",
    address: c.address ?? "",
    linkedIn: c.linkedin ?? "",
    twitter: c.twitter ?? "",
    lastActivity: c.lastActivity ?? c.last_activity ?? "",
  } as any;
}

function apiActivityToLocal(a: any): Activity {
  const ownerUserId = a.ownerUserId ?? a.owner_user_id ?? a.assignedToUserId ?? a.assigned_to_user_id ?? "";
  // Convert the API's UTC timestamp into LOCAL date / time. A naive `.split("T")[0]`
  // returns the UTC date, which can be a day off for late-evening activities in IST
  // (or any timezone east of UTC). The dashboard compares against a local TODAY so we
  // must format using local components to keep them in the same frame of reference.
  const iso = a.scheduledAt ?? a.scheduled_at ?? a.createdAt ?? a.created_at ?? "";
  const dt = iso ? new Date(iso) : null;
  const localDate = dt && !isNaN(dt.getTime())
    ? `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`
    : "";
  const localTime = dt && !isNaN(dt.getTime()) && (a.scheduledAt ?? a.scheduled_at)
    ? `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`
    : "";
  return {
    id: a.id,
    type: a.type,
    title: a.title,
    description: a.description ?? "",
    date: localDate,
    time: localTime,
    companyName: "",
    contactName: "",
    relatedType: a.relatedType ?? a.relatedTo ?? a.related_type ?? "",
    relatedId: a.relatedId ?? a.related_id ?? "",
    completed: !!(a.completedAt ?? a.completed_at),
    completedAt: a.completedAt ?? a.completed_at ?? "",
    priority: a.priority ?? "medium",
    createdAt: a.createdAt ?? a.created_at ?? "",
    assignedTo: ownerUserId,
    owner: "",
  } as any;
}

// ── Context type (same as before) ────────────────────────────────
interface CrmDataContextType {
  isLoading: boolean;
  deals: Deal[];
  customers: Customer[];
  contacts: Contact[];
  leadContactPersons: Array<{ id: string; leadId: string; name: string; phone: string | null; email: string | null; position: string | null; companyName: string }>;
  leads: Lead[];
  activities: Activity[];
  addDeal: (deal: Deal, opts?: { onSuccess?: (res: any) => void; onError?: (err: unknown) => void }) => void;
  updateDeal: (id: string, partial: Partial<Deal>) => void;
  deleteDeal: (id: string) => void;
  deleteDeals: (ids: Set<string>) => void;
  addCustomer: (customer: Customer) => void;
  updateCustomer: (id: string, partial: Partial<Customer>) => void;
  addContact: (contact: Contact) => void;
  updateContact: (id: string, partial: Partial<Contact>) => void;
  deleteContact: (id: string) => void;
  addLead: (lead: Lead, onCreated?: (id: string) => void) => void;
  addLeadChecked: (lead: Lead, force: boolean) => Promise<{ status: "created"; id?: string } | { status: "duplicate"; candidates: DuplicateCandidate[] }>;
  updateLead: (id: string, partial: Partial<Lead>) => void;
  setLeads: () => void;
  reassignLead: (leadId: string, toUserId: string, byUserId: string, options?: any) => void;
  addLeadNote: (leadId: string, body: string) => Promise<void>;
  convertLead: (leadId: string) => Promise<void>;
  deleteLead: (id: string) => Promise<void>;
  addActivity: (activity: Activity) => void;
  updateActivity: (id: string, partial: Partial<Activity>) => void;
  setActivities: () => void;
  teamMembers: TeamMember[];
  addTeamMember: (member: any) => void;
  updateTeamMember: (id: string, partial: Partial<TeamMember>) => void;
  removeTeamMember: (id: string) => void;
  setUserStatus: (id: string, status: UserStatus) => void;
  roles: RoleDefinition[];
  addRole: (role: RoleDefinition) => void;
  updateRole: (name: string, partial: Partial<RoleDefinition>) => void;
  removeRole: (name: string) => void;
  toggleRolePermission: (roleName: string, permission: Permission) => void;
  teams: Team[];
  addTeam: (team: Omit<Team, "id">) => void;
  updateTeam: (id: string, partial: Partial<Team>) => void;
  removeTeam: (id: string) => void;
  emailThreads: EmailThread[];
  emailMessages: EmailMessage[];
  sendEmail: (input: any) => void;
  emailAccounts: EmailAccount[];
  connectEmailAccount: (input: any) => void;
  disconnectEmailAccount: (id: string) => void;
  setEmailAccountStatus: (id: string, status: EmailAccountStatus, errorMessage?: string) => void;
  addStakeholder: (dealId: string, stakeholder: any) => void;
  removeStakeholder: (dealId: string, stakeholderId: string) => void;
  updateStakeholder: (dealId: string, stakeholderId: string, partial: any) => void;
  toggleChecklistItem: (dealId: string, itemId: string, currentlyCompleted: boolean) => void;
  initStageChecklist: (dealId: string, stage: DealStage) => void;
  leadSources: MasterItem[];
  leadStagesMaster: MasterItem[];
  activityTypes: MasterItem[];
  industries: MasterItem[];
  tags: MasterItem[];
  leadPriorities: MasterItem[];
  leadStatuses: MasterItem[];
  productServices: MasterItem[];
  companySizes: MasterItem[];
  emailTemplates: EmailTemplate[];
  addLeadContactPerson: (leadId: string, person: { name: string; phone?: string; email?: string; position?: string }) => Promise<void>;
  updateLeadContactPerson: (leadId: string, personId: string, person: { name: string; phone?: string; email?: string; position?: string }) => Promise<void>;
  removeLeadContactPerson: (leadId: string, personId: string) => Promise<void>;
  addMasterItem: (key: any, item: any) => void;
  updateMasterItem: (key: any, id: string, partial: any) => void;
  removeMasterItem: (key: any, id: string) => void;
  addEmailTemplate: (tpl: any) => void;
  updateEmailTemplate: (id: string, partial: any) => void;
  removeEmailTemplate: (id: string) => void;
  automationRules: AutomationRule[];
  addAutomationRule: (rule: Omit<AutomationRule, 'id' | 'triggerCount' | 'lastTriggeredAt' | 'createdAt'>) => void;
  updateAutomationRule: (id: string, partial: Partial<AutomationRule>) => void;
  removeAutomationRule: (id: string) => void;
}

const globalCrmData = globalThis as typeof globalThis & {
  __febixCrmDataContext__?: Context<CrmDataContextType | undefined>;
};

const CrmDataContext =
  globalCrmData.__febixCrmDataContext__ ?? createContext<CrmDataContextType | undefined>(undefined);

globalCrmData.__febixCrmDataContext__ = CrmDataContext;

function apiEmailTemplateToLocal(t: any): EmailTemplate {
  return {
    id: t.id,
    name: t.name,
    subject: t.subject,
    body: t.body,
    active: !!t.active,
  };
}

export function CrmDataProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  // ── Fetch real data ───────────────────────────────────────────
  // CrmDataProvider is only mounted for authenticated tenant users (see App.tsx).
  const { data: leadsData, isLoading: leadsLoading } = useQuery({
    queryKey: ["leads"],
    queryFn: () => fetchAllPages<any>("/leads"),
    staleTime: 15_000,
    retry: 1,
  });

  const { data: dealsData, isLoading: dealsLoading } = useQuery({
    queryKey: ["deals"],
    queryFn: () => fetchAllPages<any>("/deals"),
    staleTime: 15_000,
    retry: 1,
  });

  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: () => fetchAllPages<any>("/customers"),
    staleTime: 30_000,
    retry: 1,
  });

  const { data: contactsData } = useQuery({
    queryKey: ["contacts"],
    queryFn: () => fetchAllPages<any>("/contacts"),
    staleTime: 30_000,
    retry: 1,
  });

  const { data: leadContactPersonsData } = useQuery({
    queryKey: ["lead-contact-persons"],
    queryFn: () => api.get<any>("/leads/contact-persons"),
    staleTime: 30_000,
    retry: 1,
  });

  const { data: activitiesData, isLoading: activitiesLoading } = useQuery({
    queryKey: ["activities"],
    queryFn: () => fetchAllPages<any>("/activities"),
    staleTime: 15_000,
    retry: 1,
  });

  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: () => fetchAllPages<any>("/users"),
    staleTime: 30_000,
    retry: 1,
  });

  const { data: masterItemsData } = useQuery({
    queryKey: ["master-items"],
    queryFn: () => api.get<any>("/master-items"),
    staleTime: 60_000,
    retry: 1,
  });

  const { data: emailTemplatesData } = useQuery({
    queryKey: ["email-templates"],
    queryFn: () => api.get<any>("/email-templates"),
    staleTime: 60_000,
    retry: 1,
  });

  const { data: automationRulesData } = useQuery({
    queryKey: ["automation-rules"],
    queryFn: () => api.get<any>("/automation-rules"),
    staleTime: 30_000,
    retry: 1,
  });

  const { data: rolesData } = useQuery({
    queryKey: ["roles"],
    queryFn: () => api.get<any>("/roles"),
    staleTime: 60_000,
    retry: 1,
  });

  const { data: teamsData } = useQuery({
    queryKey: ["teams"],
    queryFn: () => api.get<any>("/teams"),
    staleTime: 60_000,
    retry: 1,
  });

  // ── Convert API shapes to local types ────────────────────────
  const usersRaw: any[] = usersData?.data ?? [];
  // Resolve any user identifier (internal ULID `u.id` or auth0 sub `u.auth0UserId`)
  // to a friendly display name. Notes, audit events, and stage history store the
  // auth0 sub directly, while ownership fields store the internal ULID — both must
  // map to the same person.
  const userById = (id: string) => {
    if (!id) return "";
    const u = usersRaw.find((u: any) =>
      u.id === id || u.auth0UserId === id || u.auth0_user_id === id
    );
    return u ? `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email : id;
  };
  const customerRaw: any[] = customersData?.data ?? [];
  const customerNameById = (id: string) => customerRaw.find((c: any) => c.id === id)?.name ?? "";

  const leads: Lead[] = (leadsData?.data ?? []).map((l: any) => ({
    ...apiLeadToLocal(l),
    // assignedToUserId is updated by /assignments endpoint; ownerUserId may lag behind.
    owner: userById(l.assignedToUserId ?? l.assigned_to_user_id ?? l.ownerUserId ?? l.owner_user_id ?? ""),
    enteredBy: userById(l.ownerUserId ?? l.owner_user_id ?? l.assignedToUserId ?? l.assigned_to_user_id ?? ""),
    notes: (l.notes ?? []).map((n: any) => ({
      id: n.id,
      content: n.body ?? n.content ?? "",
      author: userById(n.authorId ?? n.author_id ?? n.author ?? ""),
      createdAt: n.createdAt ?? n.created_at ?? "",
    })),
    assignmentHistory: (l.assignments ?? []).map((a: any) => ({
      id: a.id,
      fromUserId: a.fromUserId ?? null,
      fromUserName: userById(a.fromUserId ?? ""),
      toUserId: a.toUserId,
      toUserName: userById(a.toUserId ?? ""),
      byUserId: a.byUserId,
      byUserName: userById(a.byUserId ?? ""),
      at: a.occurredAt ?? a.occurred_at ?? "",
      reason: a.reason,
      transferEmailAccess: a.transferEmail ?? false,
    })),
  }));
  // dealsData kept only for activity company-name lookups (related === "deal").
  // The Deals page is now entirely backed by leads — see `deals` below.
  const customers: Customer[] = (customersData?.data ?? []).map((c: any) => ({
    ...apiCustomerToLocal(c),
    accountOwner: userById(c.accountOwnerId ?? c.account_owner_id ?? ""),
  }));
  const contacts: Contact[] = (contactsData?.data ?? []).map((c: any) => ({
    ...apiContactToLocal(c),
    customerName: customerNameById(c.customerId ?? c.customer_id ?? ""),
    owner: userById(c.ownerId ?? c.owner_id ?? ""),
  }));
  const leadContactPersons = (leadContactPersonsData?.data ?? []) as Array<{
    id: string; leadId: string; name: string; phone: string | null;
    email: string | null; position: string | null; companyName: string;
  }>;
  const activities: Activity[] = (activitiesData?.data ?? []).map((a: any) => {
    const related = a.relatedTo ?? a.related_to ?? "";
    const relatedId = a.relatedId ?? a.related_id ?? "";
    let companyName = "";
    let contactName = "";
    if (related === "lead") {
      const lead = (leadsData?.data ?? []).find((l: any) => l.id === relatedId);
      companyName = lead?.companyName ?? lead?.company_name ?? "";
    } else if (related === "deal") {
      const deal = (dealsData?.data ?? []).find((d: any) => d.id === relatedId);
      companyName = deal?.customerName ?? deal?.customer_name ?? "";
    } else if (related === "customer") {
      companyName = customerNameById(relatedId);
    } else if (related === "contact") {
      const contact = (contactsData?.data ?? []).find((c: any) => c.id === relatedId);
      contactName = contact ? `${contact.firstName ?? ""} ${contact.lastName ?? ""}`.trim() : "";
      companyName = customerNameById(contact?.customerId ?? contact?.customer_id ?? "");
    }
    const ownerUserId = a.ownerUserId ?? a.owner_user_id ?? a.assignedToUserId ?? a.assigned_to_user_id ?? "";
    return { ...apiActivityToLocal(a), companyName, contactName, owner: userById(ownerUserId) };
  });

  const teamMembers: TeamMember[] = usersRaw.map((u: any) => ({
    id: u.id,
    authId: u.auth0UserId ?? u.auth0_user_id ?? undefined,
    name: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email,
    email: u.email,
    role: u.role,
    status: u.status,
    deals: 0,
    revenue: 0,
    avatar: u.avatarUrl ?? "",
    phone: u.phone ?? "",
    lastActive: u.lastActive ?? u.last_active ?? "",
  }));

  // ── Stage-key overrides (localStorage) ───────────────────────
  // stageKey is a frontend-only concept. Some backends don't persist it, so we
  // keep a localStorage map of { [masterItemId]: { stageKey, defaultProbability } }
  // that survives both optimistic-update resets and hard page refreshes.
  const SK_STORAGE = "crm_stage_key_overrides";
  const readSkOverrides = (): Record<string, { stageKey?: string; defaultProbability?: number }> => {
    try { return JSON.parse(localStorage.getItem(SK_STORAGE) ?? "{}"); } catch { return {}; }
  };
  const writeSkOverride = (id: string, patch: { stageKey?: string; defaultProbability?: number }) => {
    const map = readSkOverrides();
    map[id] = { ...map[id], ...patch };
    localStorage.setItem(SK_STORAGE, JSON.stringify(map));
  };

  // ── Master items from API (with static fallbacks) ────────────
  const apiMasterItems: any[] = masterItemsData?.data ?? [];
  const masterByCategory = (cat: string, fallback: MasterItem[]): MasterItem[] => {
    const fromApi = apiMasterItems.filter((m: any) => m.category === cat);
    if (fromApi.length > 0) {
      const skOverrides = readSkOverrides();
      return fromApi.map((m: any) => {
        const label: string = m.value;
        // Migrate: if API row has no stageKey, match by label against defaults so
        // formula code continues to work even for tenants with older master data.
        const defaultMatch = fallback.find(
          (f) => f.label.toLowerCase() === label.toLowerCase()
        );
        const override = skOverrides[m.id] ?? {};
        return {
          id: m.id,
          label,
          color: m.color ?? undefined,
          active: m.isActive,
          order: m.sortOrder,
          showInDeals: !!(m.showInDeals ?? m.show_in_deals),
          stageKey: override.stageKey ?? m.stageKey ?? m.stage_key ?? defaultMatch?.stageKey,
          defaultProbability: override.defaultProbability ?? m.defaultProbability ?? m.default_probability ?? defaultMatch?.defaultProbability,
        };
      });
    }
    return fallback;
  };
  const apiLeadSources     = masterByCategory("lead_source",    masterLeadSources);
  const apiLeadStages      = masterByCategory("lead_stage",     masterLeadStages);

  // Stages the tenant flagged "Show in Deals" — leads at those stages surface as
  // synthetic cards in the Sales Pipeline view.
  const dealStageLabels = new Set(
    apiLeadStages.filter((s) => s.showInDeals).map((s) => s.label)
  );
  // Deals = leads whose stage is marked "Show in Deals" in Master.
  // Lead IDs are used directly — no synthetic prefix needed.
  const deals: Deal[] = dealStageLabels.size === 0 ? [] : (leadsData?.data ?? [])
    .filter((l: any) => {
      const stage = l.stage ?? "";
      return stage && dealStageLabels.has(stage);
    })
    .map((l: any) => {
      const createdIso = l.stageEnteredAt ?? l.stage_entered_at ?? l.createdAt ?? l.created_at ?? "";
      const createdMs = createdIso ? new Date(createdIso).getTime() : NaN;
      const aging = Number.isFinite(createdMs)
        ? Math.max(0, Math.floor((Date.now() - createdMs) / 86_400_000))
        : 0;
      return {
        id: l.id,
        name: l.companyName ?? l.company_name ?? "",
        companyName: l.companyName ?? l.company_name ?? "",
        customerId: "",
        value: Number(l.expectedValue ?? l.expected_value ?? 0),
        stage: (l.stage ?? "New") as any,
        expectedCloseDate: l.estimatedClosureDate ?? l.estimated_closure_date ?? "",
        probability: l.conversionProbability ?? l.conversion_probability ?? getStageProbability(l.stage ?? "New", apiLeadStages),
        owner: userById(l.assignedToUserId ?? l.assigned_to_user_id ?? l.ownerUserId ?? l.owner_user_id ?? ""),
        createdAt: l.createdAt ?? l.created_at ?? "",
        aging,
        lossReason: l.lostReason ?? l.lost_reason ?? "",
        stakeholders: [],
        lineItems: [],
        tags: l.tags ?? [],
        stageChecklist: [],
        stageHistory: [],
        leadId: l.id,
      };
    });

  const apiActivityTypes   = masterByCategory("activity_type",  masterActivityTypes);
  const apiIndustries      = masterByCategory("industry",       masterIndustries);
  const apiTags            = masterByCategory("tag",            masterTags);
  const apiLeadPriorities  = masterByCategory("lead_priority",  masterLeadPriorities);
  const apiLeadStatuses    = masterByCategory("lead_status",    masterLeadStatuses);
  const apiProductServices = masterByCategory("product_service",masterProductServices);
  const apiCompanySizes    = masterByCategory("company_size",   masterCompanySizes);
  const apiEmailTemplates: EmailTemplate[] = (emailTemplatesData?.data ?? []).length
    ? (emailTemplatesData?.data ?? []).map(apiEmailTemplateToLocal)
    : masterEmailTemplates;

  const apiAutomationRules: AutomationRule[] = (automationRulesData?.data ?? []).map((r: any) => ({
    id: r.id,
    name: r.name,
    triggerType: r.triggerType,
    conditionField: r.conditionField ?? 'any',
    conditionValue: r.conditionValue ?? null,
    actionType: r.actionType,
    actionConfig: r.actionConfig ?? {},
    isActive: !!r.isActive,
    triggerCount: Number(r.triggerCount ?? 0),
    lastTriggeredAt: r.lastTriggeredAt ?? null,
    createdAt: r.createdAt,
  }));

  // ── Mutations ────────────────────────────────────────────────
  const createLeadMut = useMutation({
    mutationFn: (d: any) => api.post("/leads", d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leads"] }),
    onError: mutationErrorHandler("Create lead"),
  });

  const updateLeadMut = useMutation({
    mutationFn: ({ id, ...d }: any) => api.patch(`/leads/${id}`, d),
    onSuccess: (_, v: any) => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      if (v?.id) qc.invalidateQueries({ queryKey: ["lead", v.id] });
      // A lead moving to "Closed" creates a customer server-side — refresh customers.
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
    onError: mutationErrorHandler("Update lead"),
  });

  const deleteLeadMut = useMutation({
    mutationFn: (id: string) => api.delete(`/leads/${id}`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["lead", id] });
      qc.invalidateQueries({ queryKey: ["deals"] });
    },
    onError: mutationErrorHandler("Delete lead"),
  });

  const createCustomerMut = useMutation({
    mutationFn: (d: any) => api.post("/customers", d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
    onError: mutationErrorHandler("Create customer"),
  });

  const updateCustomerMut = useMutation({
    mutationFn: ({ id, ...d }: any) => api.patch(`/customers/${id}`, d),
    onSuccess: (_, v: any) => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      if (v?.id) qc.invalidateQueries({ queryKey: ["customer", v.id] });
    },
    onError: mutationErrorHandler("Update customer"),
  });

  const createContactMut = useMutation({
    mutationFn: (d: any) => api.post("/contacts", d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["contacts"] }); qc.invalidateQueries({ queryKey: ["customers"] }); },
    onError: mutationErrorHandler("Create contact"),
  });

  const updateContactMut = useMutation({
    mutationFn: ({ id, ...d }: any) => api.patch(`/contacts/${id}`, d),
    onSuccess: (_, v: any) => {
      qc.invalidateQueries({ queryKey: ["contacts"] });
      if (v?.id) qc.invalidateQueries({ queryKey: ["contact", v.id] });
    },
    onError: mutationErrorHandler("Update contact"),
  });

  const deleteContactMut = useMutation({
    mutationFn: (id: string) => api.delete(`/contacts/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["contacts"] }); qc.invalidateQueries({ queryKey: ["customers"] }); },
    onError: mutationErrorHandler("Delete contact"),
  });

  const createActivityMut = useMutation({
    mutationFn: (d: any) => api.post("/activities", d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["activities"] }),
    onError: mutationErrorHandler("Create activity"),
  });

  const updateActivityMut = useMutation({
    mutationFn: ({ id, ...d }: any) => api.patch(`/activities/${id}`, d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["activities"] }),
    onError: mutationErrorHandler("Update activity"),
  });

  const addLeadNoteMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => api.post(`/leads/${id}/notes`, { body }),
    onSuccess: (newNote: any, { id }) => {
      qc.setQueryData(["leads"], (old: any) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((l: any) =>
            l.id === id
              ? { ...l, notes: [...(l.notes ?? []), newNote] }
              : l
          ),
        };
      });
      // Detail view prefers leadDetail.notes — keep that cache in sync so the
      // new note shows immediately even before refetch completes.
      qc.setQueryData(["lead", id], (old: any) => {
        if (!old) return old;
        return { ...old, notes: [newNote, ...(old.notes ?? [])] };
      });
      qc.invalidateQueries({ queryKey: ["lead", id] });
    },
    onError: mutationErrorHandler("Add lead note"),
  });

  const convertLeadMut = useMutation({
    mutationFn: (id: string) => api.post(`/leads/${id}/convert`, {}),
    onSuccess: (_, id: string) => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["lead", id] });
      qc.invalidateQueries({ queryKey: ["deals"] });
      // Conversion now creates/links a customer server-side — refresh customers.
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
    onError: mutationErrorHandler("Convert lead"),
  });

  const assignLeadMut = useMutation({
    mutationFn: ({ leadId, toUserId, reason, transferEmailAccess }: any) =>
      api.post(`/leads/${leadId}/assignments`, { toUserId, reason, transferEmailAccess }),
    onMutate: async ({ leadId, toUserId }: any) => {
      // Immediately reflect the new owner in the list view without waiting for refetch.
      await qc.cancelQueries({ queryKey: ["leads"] });
      const previous = qc.getQueryData(["leads"]);
      qc.setQueryData(["leads"], (old: any) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((l: any) =>
            l.id === leadId
              ? { ...l, assignedToUserId: toUserId, assigned_to_user_id: toUserId }
              : l
          ),
        };
      });
      return { previous };
    },
    onError: (err, _vars, context: any) => {
      if (context?.previous) qc.setQueryData(["leads"], context.previous);
      mutationErrorHandler("Assign lead")(err);
    },
    onSuccess: (_res, v: any) => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      if (v?.leadId) qc.invalidateQueries({ queryKey: ["lead", v.leadId] });
    },
  });

  // ── Context value ────────────────────────────────────────────
  // Deals are backed by the leads table — all mutations target /leads endpoints.
  const addDeal = useCallback((deal: Deal, opts?: { onSuccess?: (res: any) => void; onError?: (err: unknown) => void }) => {
    const raw: any[] = usersData?.data ?? [];
    const ownerUser = deal.owner ? raw.find((u: any) =>
      `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() === deal.owner || u.email === deal.owner
    ) : undefined;
    createLeadMut.mutate({
      companyName: deal.name || deal.companyName,
      expectedValue: deal.value,
      stage: deal.stage,
      conversionProbability: deal.probability,
      estimatedClosureDate: deal.expectedCloseDate || null,
      source: "",
      priority: "Medium",
      leadStatus: "Warm",
      ...(ownerUser ? { assignedTo: ownerUser.id } : {}),
    }, {
      onSuccess: (res) => opts?.onSuccess?.(res),
      onError: (err) => opts?.onError?.(err),
    });
  }, [createLeadMut, usersData]);

  const updateDeal = useCallback((id: string, partial: Partial<Deal>) => {
    const raw: any[] = usersData?.data ?? [];
    const ownerUser = partial.owner ? raw.find((u: any) =>
      `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() === partial.owner || u.email === partial.owner
    ) : undefined;
    updateLeadMut.mutate({
      id,
      ...(partial.companyName !== undefined || partial.name !== undefined
        ? { companyName: partial.companyName ?? partial.name }
        : {}),
      ...(partial.value !== undefined ? { expectedValue: partial.value } : {}),
      ...(partial.stage !== undefined ? { stage: partial.stage } : {}),
      ...(partial.probability !== undefined ? { conversionProbability: partial.probability } : {}),
      ...(partial.expectedCloseDate !== undefined ? { estimatedClosureDate: partial.expectedCloseDate || null } : {}),
      ...(partial.lossReason !== undefined ? { lostReason: partial.lossReason || null } : {}),
      ...(partial.owner !== undefined ? { assignedTo: ownerUser?.id ?? undefined } : {}),
    });
  }, [updateLeadMut, usersData]);

  const deleteDeal = useCallback((id: string) => deleteLeadMut.mutate(id), [deleteLeadMut]);
  const deleteDeals = useCallback((ids: Set<string>) => { ids.forEach(id => deleteLeadMut.mutate(id)); }, [deleteLeadMut]);

  const addCustomer = useCallback((customer: Customer) => {
    createCustomerMut.mutate({
      name: customer.name,
      industry: customer.industry,
      gstNumber: customer.gstNumber,
      address: customer.address,
      accountOwnerId: customer.accountOwner,
    });
  }, [createCustomerMut]);

  const updateCustomer = useCallback((id: string, partial: Partial<Customer>) => {
    updateCustomerMut.mutate({
      id,
      name: partial.name,
      industry: partial.industry,
      gstNumber: partial.gstNumber,
      address: partial.address,
    });
  }, [updateCustomerMut]);

  const addContact = useCallback((contact: Contact) => {
    const raw: any[] = usersData?.data ?? [];
    const ownerUser = raw.find((u: any) =>
      `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() === contact.owner || u.email === contact.owner
    );
    createContactMut.mutate({
      customerId: contact.customerId,
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email || null,
      phone: contact.phone || null,
      position: contact.position || null,
      ownerId: ownerUser?.id ?? null,
      description: contact.description || null,
      address: contact.address || null,
      linkedin: contact.linkedIn || null,
      twitter: contact.twitter || null,
    });
  }, [createContactMut, usersData]);

  const updateContact = useCallback((id: string, partial: Partial<Contact>) => {
    const raw: any[] = usersData?.data ?? [];
    const ownerUser = partial.owner ? raw.find((u: any) =>
      `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() === partial.owner || u.email === partial.owner
    ) : undefined;
    updateContactMut.mutate({
      id,
      ...(partial.customerId !== undefined ? { customerId: partial.customerId } : {}),
      ...(partial.firstName !== undefined ? { firstName: partial.firstName } : {}),
      ...(partial.lastName !== undefined ? { lastName: partial.lastName } : {}),
      ...(partial.email !== undefined ? { email: partial.email || null } : {}),
      ...(partial.phone !== undefined ? { phone: partial.phone || null } : {}),
      ...(partial.position !== undefined ? { position: partial.position || null } : {}),
      ...(partial.owner !== undefined ? { ownerId: ownerUser?.id ?? null } : {}),
      ...(partial.description !== undefined ? { description: partial.description || null } : {}),
      ...(partial.address !== undefined ? { address: partial.address || null } : {}),
      ...(partial.linkedIn !== undefined ? { linkedin: partial.linkedIn || null } : {}),
      ...(partial.twitter !== undefined ? { twitter: partial.twitter || null } : {}),
    });
  }, [updateContactMut, usersData]);

  const deleteContact = useCallback((id: string) => deleteContactMut.mutate(id), [deleteContactMut]);

  const buildLeadPayload = useCallback((lead: Lead, force?: boolean) => ({
    companyName: lead.companyName,
    email: lead.email || null,
    phone: lead.phone || null,
    source: lead.source,
    expectedValue: lead.expectedValue,
    stage: lead.stage,
    assignedTo: lead.assignedTo,
    nextFollowUp: lead.nextFollowUp || null,
    priority: lead.priority ?? "Medium",
    leadStatus: lead.leadStatus ?? "Warm",
    requirementDescription: lead.requirementDescription || null,
    // Prefer followUpNotes; fall back to legacy `description` (Additional Notes field).
    followUpNotes: (lead.followUpNotes || (lead as any).description || null) || null,
    reminderAt: lead.reminderAt ? (lead.reminderAt.length === 16 ? `${lead.reminderAt}:00.000Z` : lead.reminderAt) : null,
    productService: lead.productService || null,
    companySize: lead.companySize || null,
    conversionProbability: lead.conversionProbability ?? 0,
    estimatedClosureDate: lead.estimatedClosureDate || null,
    ...(force ? { force: true } : {}),
  }), []);

  const addLead = useCallback((lead: Lead, onCreated?: (id: string) => void) => {
    createLeadMut.mutate(buildLeadPayload(lead), {
      onSuccess: (res: any) => {
        qc.invalidateQueries({ queryKey: ["leads"] });
        if (onCreated && res?.id) onCreated(res.id);
      },
    });
  }, [createLeadMut, qc, buildLeadPayload]);

  // Duplicate-aware create: calls the API directly (not via the shared mutation,
  // so the global error toast doesn't fire) and returns the 409 candidates so the
  // caller can confirm before forcing the insert.
  const addLeadChecked = useCallback(async (
    lead: Lead, force: boolean,
  ): Promise<{ status: "created"; id?: string } | { status: "duplicate"; candidates: DuplicateCandidate[] }> => {
    try {
      const res: any = await api.post("/leads", buildLeadPayload(lead, force));
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
      return { status: "created", id: res?.id };
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && (e.body as any)?.error === "duplicate") {
        return { status: "duplicate", candidates: ((e.body as any)?.candidates ?? []) as DuplicateCandidate[] };
      }
      throw e;
    }
  }, [qc, buildLeadPayload]);

  const updateLead = useCallback((id: string, partial: Partial<Lead>) => {
    const raw: any[] = usersData?.data ?? [];
    // Resolve owner name → user ID. Check both composed full-name and email to handle
    // all user types (partners, agents, etc.).
    const ownerUser = partial.owner ? (
      raw.find((u: any) =>
        `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() === partial.owner ||
        u.email === partial.owner ||
        u.name === partial.owner
      )
    ) : undefined;
    // If caller passed a raw user ID in assignedTo, use it directly (bypasses name lookup).
    const resolvedAssignedTo = partial.assignedTo ?? ownerUser?.id;
    updateLeadMut.mutate({
      id,
      companyName: partial.companyName,
      // Nullable string fields — backend Zod schema rejects "" so coerce blanks to null
      // and only include the key when the caller explicitly set it.
      ...(partial.email !== undefined ? { email: partial.email || null } : {}),
      ...(partial.phone !== undefined ? { phone: partial.phone || null } : {}),
      ...(partial.nextFollowUp !== undefined ? { nextFollowUp: partial.nextFollowUp || null } : {}),
      ...(partial.lostReason !== undefined ? { lostReason: partial.lostReason || null } : {}),
      source: partial.source,
      expectedValue: partial.expectedValue,
      stage: partial.stage,
      bantScore: partial.bantScore,
      tags: partial.tags,
      ...((partial.owner !== undefined || partial.assignedTo !== undefined) && resolvedAssignedTo
        ? { assignedTo: resolvedAssignedTo }
        : {}),
      ...(partial.priority !== undefined ? { priority: partial.priority } : {}),
      ...(partial.leadStatus !== undefined ? { leadStatus: partial.leadStatus } : {}),
      ...(partial.requirementDescription !== undefined ? { requirementDescription: partial.requirementDescription || null } : {}),
      ...(partial.followUpNotes !== undefined ? { followUpNotes: partial.followUpNotes || null } : {}),
      ...(partial.reminderAt !== undefined ? { reminderAt: partial.reminderAt ? (partial.reminderAt.length === 16 ? `${partial.reminderAt}:00.000Z` : partial.reminderAt) : null } : {}),
      ...(partial.productService !== undefined ? { productService: partial.productService || null } : {}),
      ...(partial.companySize !== undefined ? { companySize: partial.companySize || null } : {}),
      ...(partial.conversionProbability !== undefined ? { conversionProbability: partial.conversionProbability } : {}),
      ...(partial.estimatedClosureDate !== undefined ? { estimatedClosureDate: partial.estimatedClosureDate || null } : {}),
    });
  }, [updateLeadMut, usersData, teamMembers]);

  const setLeads = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["leads"] });
  }, [qc]) as any;

  const reassignLead = useCallback(async (leadId: string, toUserId: string, _byUserId: string, options: any = {}) => {
    await assignLeadMut.mutateAsync({ leadId, toUserId, reason: options.reason, transferEmailAccess: options.transferEmailAccess });
  }, [assignLeadMut]);

  const addLeadNote = useCallback(async (leadId: string, body: string) => {
    await addLeadNoteMut.mutateAsync({ id: leadId, body });
  }, [addLeadNoteMut]);

  const convertLead = useCallback(async (leadId: string) => {
    await convertLeadMut.mutateAsync(leadId);
  }, [convertLeadMut]);

  const deleteLead = useCallback(async (id: string) => {
    await deleteLeadMut.mutateAsync(id);
  }, [deleteLeadMut]);

  const addActivity = useCallback((activity: Activity) => {
    createActivityMut.mutate({
      type: activity.type,
      title: activity.title,
      description: activity.description,
      scheduledAt: activity.date ? `${activity.date}T${activity.time || "09:00"}:00.000Z` : null,
      relatedTo: activity.relatedType ?? null,
      relatedId: activity.relatedId ?? null,
      priority: (activity as any).priority ?? "medium",
    });
  }, [createActivityMut]);

  const updateActivity = useCallback((id: string, partial: Partial<Activity>) => {
    updateActivityMut.mutate({
      id,
      title: partial.title,
      description: partial.description,
      completedAt: partial.completed ? new Date().toISOString() : null,
    });
  }, [updateActivityMut]);

  const setActivities = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["activities"] });
  }, [qc]) as any;

  const influenceToInt = (v: any): number => {
    if (typeof v === "number") return v;
    const map: Record<string, number> = { High: 5, Medium: 3, Low: 1 };
    return map[v] ?? 3;
  };

  // Stakeholders
  const addStakeholder = useCallback((dealId: string, s: any) => {
    const payload = { ...s, influence: influenceToInt(s.influence) };
    api.post(`/deals/${dealId}/stakeholders`, payload).then(() => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    }).catch(mutationErrorHandler("Add stakeholder"));
  }, [qc]);

  const removeStakeholder = useCallback((dealId: string, sId: string) => {
    api.delete(`/deals/${dealId}/stakeholders/${sId}`).then(() => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    }).catch(mutationErrorHandler("Remove stakeholder"));
  }, [qc]);

  const updateStakeholder = useCallback((dealId: string, sId: string, partial: any) => {
    const payload = { ...partial };
    if (partial.influence !== undefined) payload.influence = influenceToInt(partial.influence);
    api.patch(`/deals/${dealId}/stakeholders/${sId}`, payload).then(() => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
      toast.success("Stakeholder updated");
    }).catch(mutationErrorHandler("Update stakeholder"));
  }, [qc]);

  // User management — API-backed
  const inviteUserMut = useMutation({
    mutationFn: (d: any) => api.post("/users", d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
    onError: mutationErrorHandler("Invite user"),
  });
  const updateUserMut = useMutation({
    mutationFn: ({ id, ...d }: any) => api.patch(`/users/${id}`, d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
    onError: mutationErrorHandler("Update user"),
  });
  const deleteUserMut = useMutation({
    mutationFn: (id: string) => api.delete(`/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
    onError: mutationErrorHandler("Delete user"),
  });

  const addTeamMember = useCallback((member: any) => {
    const fullName: string = member.name ?? "";
    const [firstName, ...rest] = fullName.split(" ").filter(Boolean);
    const lastName = rest.join(" ") || firstName || member.email?.split("@")[0] || "User";
    inviteUserMut.mutate({
      email: member.email,
      firstName: firstName || lastName,
      lastName,
      role: member.role,
      managerId: member.managerId || undefined,
      phone: member.phone || undefined,
    });
  }, [inviteUserMut]);

  const updateTeamMember = useCallback((id: string, partial: Partial<TeamMember>) => {
    const payload: any = {};
    const userPatch = partial as Partial<TeamMember> & { phone?: string };
    if (partial.name !== undefined) {
      const parts = (partial.name as string).split(" ").filter(Boolean);
      payload.firstName = parts[0] ?? "";
      payload.lastName = parts.slice(1).join(" ") || parts[0] || "";
    }
    if (partial.role !== undefined) payload.role = partial.role;
    if (partial.status !== undefined) payload.status = partial.status;
    if (userPatch.phone !== undefined) payload.phone = userPatch.phone;
    if (partial.avatar !== undefined) payload.avatarUrl = partial.avatar;
    updateUserMut.mutate({ id, ...payload });
  }, [updateUserMut]);

  const removeTeamMember = useCallback((id: string) => {
    deleteUserMut.mutate(id);
  }, [deleteUserMut]);

  const setUserStatus = useCallback((id: string, status: UserStatus) => {
    updateUserMut.mutate({ id, status });
  }, [updateUserMut]);

  // ── Roles ────────────────────────────────────────────────────
  const rawRoles: any[] = Array.isArray(rolesData) ? rolesData : (rolesData?.data ?? []);
  const apiRoles: RoleDefinition[] = rawRoles.length > 0
    ? rawRoles.map((r: any) => ({
        name: r.name,
        color: r.color ?? "hsl(var(--primary))",
        permissions: (r.permissions ?? []) as Permission[],
        isBuiltIn: !!r.isSystem,
      } as RoleDefinition))
    : defaultRoles;
  const roleIdByName = (name: string) => rawRoles.find((r: any) => r.name === name)?.id;
  const invalidateRoles = () => qc.invalidateQueries({ queryKey: ["roles"] });

  const createRoleMut = useMutation({
    mutationFn: (d: any) => api.post("/roles", d),
    onSuccess: invalidateRoles,
    onError: mutationErrorHandler("Create role"),
  });
  const updateRoleMut = useMutation({
    mutationFn: ({ id, ...d }: any) => api.patch(`/roles/${id}`, d),
    onSuccess: invalidateRoles,
    onError: mutationErrorHandler("Update role"),
  });
  const deleteRoleMut = useMutation({
    mutationFn: (id: string) => api.delete(`/roles/${id}`),
    onSuccess: invalidateRoles,
    onError: mutationErrorHandler("Delete role"),
  });
  const toggleRolePermMut = useMutation({
    mutationFn: ({ id, permission }: { id: string; permission: string }) =>
      api.post(`/roles/${id}/permissions/toggle`, { permission }),
    onSuccess: invalidateRoles,
    onError: mutationErrorHandler("Toggle role permission"),
  });

  const addRole = useCallback((role: RoleDefinition) => {
    createRoleMut.mutate({
      name: role.name,
      description: undefined,
      color: role.color,
      permissions: role.permissions,
    });
  }, [createRoleMut]);

  const updateRole = useCallback((name: string, partial: Partial<RoleDefinition>) => {
    const id = roleIdByName(name);
    if (!id) return;
    const payload: any = {};
    if (partial.name !== undefined) payload.name = partial.name;
    if (partial.color !== undefined) payload.color = partial.color;
    if (partial.permissions !== undefined) payload.permissions = partial.permissions;
    updateRoleMut.mutate({ id, ...payload });
  }, [updateRoleMut, rawRoles]);

  const removeRole = useCallback((name: string) => {
    const id = roleIdByName(name);
    if (!id) return;
    deleteRoleMut.mutate(id);
  }, [deleteRoleMut, rawRoles]);

  const toggleRolePermission = useCallback((roleName: string, permission: Permission) => {
    const id = roleIdByName(roleName);
    if (!id) return;
    toggleRolePermMut.mutate({ id, permission });
  }, [toggleRolePermMut, rawRoles]);

  // ── Teams ────────────────────────────────────────────────────
  const rawTeams: any[] = Array.isArray(teamsData) ? teamsData : (teamsData?.data ?? []);
  const apiTeams: Team[] = rawTeams.length > 0
    ? rawTeams.map((t: any) => ({
        id: t.id,
        name: t.name,
        managerId: t.managerId ?? t.manager_id ?? "",
        memberIds: t.memberIds ?? t.member_ids ?? [],
      } as Team))
    : defaultTeams;
  const invalidateTeams = () => qc.invalidateQueries({ queryKey: ["teams"] });

  const createTeamMut = useMutation({
    mutationFn: (d: any) => api.post("/teams", d),
    onSuccess: invalidateTeams,
    onError: mutationErrorHandler("Create team"),
  });
  const updateTeamMut = useMutation({
    mutationFn: ({ id, ...d }: any) => api.patch(`/teams/${id}`, d),
    onSuccess: invalidateTeams,
    onError: mutationErrorHandler("Update team"),
  });
  const deleteTeamMut = useMutation({
    mutationFn: (id: string) => api.delete(`/teams/${id}`),
    onSuccess: invalidateTeams,
    onError: mutationErrorHandler("Delete team"),
  });

  const addTeam = useCallback((team: Omit<Team, "id">) => {
    createTeamMut.mutate({
      name: team.name,
      managerId: team.managerId,
      description: (team as any).description,
      memberIds: team.memberIds ?? [],
    });
  }, [createTeamMut]);

  const updateTeam = useCallback((id: string, partial: Partial<Team>) => {
    const payload: any = {};
    if (partial.name !== undefined) payload.name = partial.name;
    if (partial.managerId !== undefined) payload.managerId = partial.managerId;
    if (partial.memberIds !== undefined) payload.memberIds = partial.memberIds;
    if ((partial as any).description !== undefined) payload.description = (partial as any).description;
    updateTeamMut.mutate({ id, ...payload });
  }, [updateTeamMut]);

  const removeTeam = useCallback((id: string) => {
    deleteTeamMut.mutate(id);
  }, [deleteTeamMut]);

  // Stage checklist — API-backed. Labels for "Load Default Checklist" come from the
  // canonical DEAL_STAGE_CHECKLISTS map; if the stage isn't in the map (custom master
  // label), the seed is a no-op until the UI grows an "Add custom item" affordance.
  const initStageChecklist = useCallback((dealId: string, stage: DealStage) => {
    const labels = DEAL_STAGE_CHECKLISTS[stage as keyof typeof DEAL_STAGE_CHECKLISTS] ?? [];
    if (labels.length === 0) {
      toast.error(`No default checklist defined for stage "${stage}"`);
      return;
    }
    api.post(`/deals/${dealId}/checklist`, { stage, labels })
      .then(() => qc.invalidateQueries({ queryKey: ["deals"] }))
      .catch(mutationErrorHandler("Load default checklist"));
  }, [qc]);

  // Toggle a single checklist item's completion. Backend infers the new state from the
  // current row, but we pass the desired boolean so the call is idempotent.
  const toggleChecklistItem = useCallback((dealId: string, itemId: string, currentlyCompleted: boolean) => {
    if (!itemId) return;
    api.patch(`/deals/${dealId}/checklist/${itemId}`, { completed: !currentlyCompleted })
      .then(() => qc.invalidateQueries({ queryKey: ["deals"] }))
      .catch(mutationErrorHandler("Toggle checklist item"));
  }, [qc]);

  // Email accounts — real API
  const { data: rawEmailAccounts } = useQuery({
    queryKey: ["emailAccounts"],
    queryFn: () => emailAccountsApi.list(),
    staleTime: 30_000,
  });

  const apiEmailAccounts: EmailAccount[] = (rawEmailAccounts ?? []).map((a: ApiEmailAccount) => ({
    id: a.id,
    userId: a.userId,
    provider: a.provider as "gmail" | "outlook" | "imap",
    emailAddress: a.emailAddress,
    displayName: a.displayName,
    status: a.status as "connected" | "disconnected" | "error",
    lastSyncAt: a.lastSyncAt ?? "",
    errorMessage: a.errorMessage ?? undefined,
  }));

  const connectEmailAccount = useCallback(async (input: { provider: "gmail" | "outlook" | "imap" }) => {
    try {
      const ALLOWED_OAUTH_ORIGINS = [
        "https://accounts.google.com",
        "https://login.microsoftonline.com",
      ];
      const safeRedirect = (url: string) => {
        try {
          const u = new URL(url);
          if (!ALLOWED_OAUTH_ORIGINS.some(o => u.origin === o)) {
            throw new Error(`Blocked OAuth redirect to untrusted origin: ${u.origin}`);
          }
          window.location.href = url;
        } catch (e) {
          mutationErrorHandler("Connect email account")(e);
        }
      };
      if (input.provider === "gmail") {
        const { authorizationUrl } = await emailAccountsApi.initGmailOAuth();
        safeRedirect(authorizationUrl);
      } else if (input.provider === "outlook") {
        const { authorizationUrl } = await emailAccountsApi.initMicrosoftOAuth();
        safeRedirect(authorizationUrl);
      }
    } catch (err) {
      mutationErrorHandler("Connect email account")(err);
    }
  }, []);

  const disconnectEmailAccount = useCallback((id: string) => {
    emailAccountsApi.disconnect(id)
      .then(() => qc.invalidateQueries({ queryKey: ["emailAccounts"] }))
      .catch(mutationErrorHandler("Disconnect email account"));
  }, [qc]);

  const setEmailAccountStatus = useCallback((id: string, status: EmailAccountStatus, errorMessage?: string) => {
    emailAccountsApi.updateStatus(id, status, errorMessage)
      .then(() => qc.invalidateQueries({ queryKey: ["emailAccounts"] }))
      .catch(mutationErrorHandler("Update email account status"));
  }, [qc]);

  const sendEmail = useCallback((input: any) => {
    emailAccountsApi.send(input)
      .then(() => {
        qc.invalidateQueries({ queryKey: ["emailThreads", input.leadId] });
      })
      .catch(mutationErrorHandler("Send email"));
  }, [qc]);

  // Lead contact persons
  const addLeadContactPerson = useCallback(async (leadId: string, person: { name: string; phone?: string; email?: string; position?: string }) => {
    try {
      await api.post(`/leads/${leadId}/contact-persons`, {
        name: person.name,
        phone: person.phone || null,
        email: person.email || null,
        position: person.position || null,
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["leads"] }),
        qc.invalidateQueries({ queryKey: ["lead", leadId] }),
        qc.invalidateQueries({ queryKey: ["lead-contact-persons"] }),
      ]);
    } catch (err) {
      mutationErrorHandler("Add lead contact")(err);
      throw err;
    }
  }, [qc]);

  const updateLeadContactPerson = useCallback(async (
    leadId: string,
    personId: string,
    person: { name: string; phone?: string; email?: string; position?: string },
  ) => {
    try {
      await api.patch(`/leads/${leadId}/contact-persons/${personId}`, {
        name: person.name,
        phone: person.phone || null,
        email: person.email || null,
        position: person.position || null,
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["leads"] }),
        qc.invalidateQueries({ queryKey: ["lead", leadId] }),
        qc.invalidateQueries({ queryKey: ["lead-contact-persons"] }),
      ]);
    } catch (err) {
      mutationErrorHandler("Update lead contact")(err);
      throw err;
    }
  }, [qc]);

  const removeLeadContactPerson = useCallback(async (leadId: string, personId: string) => {
    try {
      await api.delete(`/leads/${leadId}/contact-persons/${personId}`);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["leads"] }),
        qc.invalidateQueries({ queryKey: ["lead", leadId] }),
        qc.invalidateQueries({ queryKey: ["lead-contact-persons"] }),
      ]);
    } catch (err) {
      mutationErrorHandler("Remove lead contact")(err);
      throw err;
    }
  }, [qc]);

  // Master items — real API
  const masterKeyToCategory: Record<string, string> = {
    leadSources: "lead_source",
    leadStages: "lead_stage",
    activityTypes: "activity_type",
    industries: "industry",
    tags: "tag",
    leadPriorities: "lead_priority",
    leadStatuses: "lead_status",
    productServices: "product_service",
    companySizes: "company_size",
  };
  const invalidateMaster = () => qc.invalidateQueries({ queryKey: ["master-items"] });

  const patchMasterCache = useCallback((updater: (items: any[]) => any[]) => {
    qc.setQueryData(["master-items"], (old: any) => {
      if (!old?.data) return old;
      return { ...old, data: updater(old.data) };
    });
  }, [qc]);

  const addMasterItem = useCallback((key: string, item: any) => {
    const category = masterKeyToCategory[key];
    if (!category) return;
    const tempId = `temp-${Date.now()}`;
    const apiItem = {
      id: tempId, category,
      value: item.label ?? item.value,
      color: item.color ?? null,
      sortOrder: item.order ?? item.sortOrder ?? 0,
      isActive: item.active !== false,
      showInDeals: item.showInDeals ?? false,
      stageKey: item.stageKey ?? null,
      defaultProbability: item.defaultProbability ?? null,
    };
    patchMasterCache((items) => [...items, apiItem]);
    api.post("/master-items", {
      category,
      value: apiItem.value,
      color: apiItem.color,
      sortOrder: apiItem.sortOrder,
      isActive: apiItem.isActive,
      showInDeals: apiItem.showInDeals,
      stageKey: apiItem.stageKey,
      stage_key: apiItem.stageKey,
      defaultProbability: apiItem.defaultProbability,
      default_probability: apiItem.defaultProbability,
    }).then((res: any) => {
      // Persist stageKey for the real ID returned by the backend
      const realId = res?.id ?? res?.data?.id;
      if (realId && (apiItem.stageKey || apiItem.defaultProbability != null)) {
        writeSkOverride(realId, { stageKey: apiItem.stageKey, defaultProbability: apiItem.defaultProbability });
      }
      invalidateMaster();
    }).catch((e) => { patchMasterCache((items) => items.filter((i) => i.id !== tempId)); mutationErrorHandler(`Add ${key}`)(e); });
  }, [qc, patchMasterCache]);

  const updateMasterItem = useCallback((key: string, id: string, partial: any) => {
    // Optimistic update — map MasterItem fields to API field names
    patchMasterCache((items) => items.map((item) => {
      if (item.id !== id) return item;
      return {
        ...item,
        ...(partial.label      !== undefined && { value:              partial.label }),
        ...(partial.active     !== undefined && { isActive:           partial.active }),
        ...(partial.color      !== undefined && { color:              partial.color }),
        ...(partial.order      !== undefined && { sortOrder:          partial.order }),
        ...(partial.showInDeals !== undefined && { showInDeals:       partial.showInDeals }),
        ...(partial.stageKey   !== undefined && { stageKey:           partial.stageKey }),
        ...(partial.defaultProbability !== undefined && { defaultProbability: partial.defaultProbability }),
      };
    }));
    const payload: any = {};
    if (partial.active !== undefined) payload.isActive = partial.active;
    if (partial.label !== undefined) payload.value = partial.label;
    if (partial.color !== undefined) payload.color = partial.color;
    if (partial.order !== undefined) payload.sortOrder = partial.order;
    if (partial.showInDeals !== undefined) payload.showInDeals = partial.showInDeals;
    // Send both camelCase and snake_case — different backend implementations accept different forms
    if (partial.stageKey !== undefined) { payload.stageKey = partial.stageKey; payload.stage_key = partial.stageKey; }
    if (partial.defaultProbability !== undefined) { payload.defaultProbability = partial.defaultProbability; payload.default_probability = partial.defaultProbability; }
    // Persist stageKey/defaultProbability locally so they survive backend refetches
    if (partial.stageKey !== undefined || partial.defaultProbability !== undefined) {
      writeSkOverride(id, {
        ...(partial.stageKey !== undefined && { stageKey: partial.stageKey }),
        ...(partial.defaultProbability !== undefined && { defaultProbability: partial.defaultProbability }),
      });
    }
    api.patch(`/master-items/${id}`, payload).then(invalidateMaster).catch(mutationErrorHandler(`Update ${key}`));
  }, [qc, patchMasterCache]);

  const removeMasterItem = useCallback((key: string, id: string) => {
    patchMasterCache((items) => items.filter((i) => i.id !== id));
    api.delete(`/master-items/${id}`).then(invalidateMaster).catch((e) => { invalidateMaster(); mutationErrorHandler(`Delete ${key}`)(e); });
  }, [qc, patchMasterCache]);
  const invalidateEmailTemplates = () => qc.invalidateQueries({ queryKey: ["email-templates"] });
  const addEmailTemplate = useCallback((tpl: any) => {
    api.post("/email-templates", {
      name: tpl.name,
      subject: tpl.subject,
      body: tpl.body,
      active: tpl.active !== false,
    }).then(invalidateEmailTemplates).catch(mutationErrorHandler("Add email template"));
  }, [qc]);
  const updateEmailTemplate = useCallback((id: string, partial: any) => {
    api.patch(`/email-templates/${id}`, partial).then(invalidateEmailTemplates).catch(mutationErrorHandler("Update email template"));
  }, [qc]);
  const removeEmailTemplate = useCallback((id: string) => {
    api.delete(`/email-templates/${id}`).then(invalidateEmailTemplates).catch(mutationErrorHandler("Delete email template"));
  }, [qc]);

  const invalidateAutomationRules = () => qc.invalidateQueries({ queryKey: ["automation-rules"] });
  const addAutomationRule = useCallback((rule: Omit<AutomationRule, 'id' | 'triggerCount' | 'lastTriggeredAt' | 'createdAt'>) => {
    api.post("/automation-rules", {
      name: rule.name,
      triggerType: rule.triggerType,
      conditionField: rule.conditionField,
      conditionValue: rule.conditionValue ?? null,
      actionType: rule.actionType,
      actionConfig: rule.actionConfig,
      isActive: rule.isActive !== false,
    }).then(invalidateAutomationRules).catch(mutationErrorHandler("Add automation rule"));
  }, [qc]);
  const updateAutomationRule = useCallback((id: string, partial: Partial<AutomationRule>) => {
    api.patch(`/automation-rules/${id}`, partial).then(invalidateAutomationRules).catch(mutationErrorHandler("Update automation rule"));
  }, [qc]);
  const removeAutomationRule = useCallback((id: string) => {
    api.delete(`/automation-rules/${id}`).then(invalidateAutomationRules).catch(mutationErrorHandler("Delete automation rule"));
  }, [qc]);

  // Combined loading state from key queries
  const isLoading = leadsLoading || dealsLoading || customersLoading || activitiesLoading;

  return (
    <CrmDataContext.Provider value={{
      isLoading,
      deals, customers, contacts, leadContactPersons, leads, activities,
      addDeal, updateDeal, deleteDeal, deleteDeals,
      addCustomer, updateCustomer,
      addContact, updateContact, deleteContact,
      addLead, addLeadChecked, updateLead, setLeads, reassignLead, addLeadNote, convertLead, deleteLead,
      addActivity, updateActivity, setActivities,
      teamMembers,
      addTeamMember, updateTeamMember, removeTeamMember, setUserStatus,
      roles: apiRoles,
      addRole, updateRole, removeRole, toggleRolePermission,
      teams: apiTeams,
      addTeam, updateTeam, removeTeam,
      emailThreads: [],
      emailMessages: [],
      sendEmail,
      emailAccounts: apiEmailAccounts,
      connectEmailAccount, disconnectEmailAccount, setEmailAccountStatus,
      addStakeholder, removeStakeholder, updateStakeholder,
      toggleChecklistItem, initStageChecklist,
      leadSources: apiLeadSources,
      leadStagesMaster: apiLeadStages,
      activityTypes: apiActivityTypes,
      industries: apiIndustries,
      tags: apiTags,
      leadPriorities: apiLeadPriorities,
      leadStatuses: apiLeadStatuses,
      productServices: apiProductServices,
      companySizes: apiCompanySizes,
      emailTemplates: apiEmailTemplates,
      addLeadContactPerson, updateLeadContactPerson, removeLeadContactPerson,
      addMasterItem, updateMasterItem, removeMasterItem,
      addEmailTemplate, updateEmailTemplate, removeEmailTemplate,
      automationRules: apiAutomationRules,
      addAutomationRule, updateAutomationRule, removeAutomationRule,
    }}>
      {children}
    </CrmDataContext.Provider>
  );
}

export function useCrmData() {
  const ctx = useContext(CrmDataContext);
  if (!ctx) throw new Error("useCrmData must be used within CrmDataProvider");
  return ctx;
}

export type MasterKey = "leadSources" | "leadStages" | "activityTypes" | "industries" | "tags" | "leadPriorities" | "leadStatuses" | "productServices" | "companySizes";
